import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback, startTransition, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { subscribeChannel } from "@/lib/realtime/channelManager";

export interface AccessibleAccount {
  account_id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  is_owner: boolean;
  chat_mode: "read_only" | "actions";
  permissions: Record<string, "none" | "busy_only" | "read" | "edit">;
  /** Empty array = all organizations of the account are visible. */
  allowed_organization_ids: string[];
}

interface AccountContextValue {
  /** Currently active account id (defaults to user.id). */
  activeAccountId: string | null;
  /** True when the active account is the user's own account. */
  isOwnAccount: boolean;
  /** All accounts the current user can access. */
  accounts: AccessibleAccount[];
  /** Active account details. */
  activeAccount: AccessibleAccount | null;
  /** Switch to another account (must be in accessible list). */
  switchAccount: (accountId: string) => void;
  /** Reload accessible accounts list. */
  refresh: () => Promise<void>;
  loading: boolean;
  /** True briefly while switching accounts, for UI fade transitions. */
  isSwitching: boolean;
  /** Permission helper for the active account. */
  hasPermission: (module: string, level: "read" | "edit") => boolean;
}

const AccountContext = createContext<AccountContextValue | undefined>(undefined);

const STORAGE_KEY = "thiia.activeAccountId";
const BROADCAST_CHANNEL = "thiia.activeAccount";

function readStoredAccountId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredAccountId(id: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* quota / privacy mode → ignore */
  }
}

function accountsEqual(a: AccessibleAccount[], b: AccessibleAccount[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.account_id !== y.account_id ||
      x.display_name !== y.display_name ||
      x.email !== y.email ||
      x.avatar_url !== y.avatar_url ||
      x.is_owner !== y.is_owner ||
      x.chat_mode !== y.chat_mode
    ) return false;
    // Shallow compare orgs + permissions (order-independent by key count + entries).
    if (x.allowed_organization_ids.length !== y.allowed_organization_ids.length) return false;
    for (let j = 0; j < x.allowed_organization_ids.length; j++) {
      if (x.allowed_organization_ids[j] !== y.allowed_organization_ids[j]) return false;
    }
    const xk = Object.keys(x.permissions || {});
    const yk = Object.keys(y.permissions || {});
    if (xk.length !== yk.length) return false;
    for (const k of xk) {
      if ((x.permissions as any)[k] !== (y.permissions as any)[k]) return false;
    }
  }
  return true;
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [accounts, setAccounts] = useState<AccessibleAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(() => readStoredAccountId());
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setAccounts([]);
      setActiveAccountId(null);
      setLoading(false);
      setInitialized(true);
      return;
    }
    // Only flip `loading` to true on the *first* resolution. Subsequent
    // refreshes (token refresh, realtime, account switch) must stay silent
    // so the rest of the app (ModuleGuard, KeepAliveOutlet) doesn't flash.
    if (!initialized) setLoading(true);
    const { data, error } = await (supabase as any).rpc("get_accessible_accounts", {
      _user_id: user.id,
    });
    let nextAccounts: AccessibleAccount[];
    if (error) {
      console.error("get_accessible_accounts error:", error);
      // Fallback: just self
      nextAccounts = [
        {
          account_id: user.id,
          display_name: user.user_metadata?.display_name ?? null,
          email: user.email ?? null,
          avatar_url: null,
          is_owner: true,
          chat_mode: "actions",
          permissions: {},
          allowed_organization_ids: [],
        },
      ];
    } else {
      nextAccounts = (data || []).map((row: any) => ({
        ...row,
        allowed_organization_ids: Array.isArray(row.allowed_organization_ids)
          ? row.allowed_organization_ids
          : [],
      })) as AccessibleAccount[];
    }

    // Sélection à préserver au maximum :
    //  - Au premier chargement, on démarre sur le compte persistant (localStorage)
    //    s'il reste accessible, sinon sur le compte propre de l'utilisateur.
    //  - Sur tout refresh ultérieur (realtime, token refresh, refetch focus…),
    //    on NE réinitialise PAS activeAccountId. On conserve la sélection en
    //    cours tant qu'elle reste présente dans la liste des comptes
    //    accessibles. Elle n'est ré-alignée que si le compte actif a été
    //    révoqué (disparu de la liste renvoyée par le RPC).
    setAccounts((prev) => (accountsEqual(prev, nextAccounts) ? prev : nextAccounts));
    setActiveAccountId((prev) => {
      const stored = readStoredAccountId();
      if (prev && nextAccounts.some((a) => a.account_id === prev)) {
        // Toujours accessible → on garde la sélection courante intacte.
        if (prev !== stored) writeStoredAccountId(prev);
        return prev;
      }
      // Premier init OU compte actif révoqué → on retombe sur stored/self.
      const valid = stored && nextAccounts.some((a) => a.account_id === stored);
      const next = valid ? stored! : user.id;
      if (next !== stored) writeStoredAccountId(next);
      return next;
    });


    setLoading(false);
    setInitialized(true);
  }, [user, initialized]);

  useEffect(() => {
    refresh();
    // We intentionally only depend on `user?.id`, NOT on the `refresh`
    // callback identity — `refresh` is recreated whenever `user` changes
    // reference (e.g. on every Supabase TOKEN_REFRESHED event, even when the
    // logical user is the same). Depending on `refresh` here would re-run a
    // full RPC + flash on every token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);


  // Throttled + trailing-debounced version of `refresh` for realtime bursts.
  // - Coalesces multiple postgres_changes events fired in quick succession
  //   (batch permission update, invitation acceptance, etc.) into a single
  //   RPC call.
  // - Enforces a minimum interval between two effective refreshes so a noisy
  //   emitter can't flood the UI and cause the workspace list to blink.
  const lastRunRef = useRef(0);
  const pendingTimerRef = useRef<number | null>(null);
  const MIN_INTERVAL_MS = 1500;
  const TRAILING_DELAY_MS = 400;

  const scheduleRefresh = useCallback(() => {
    const now = Date.now();
    const sinceLast = now - lastRunRef.current;
    const run = () => {
      lastRunRef.current = Date.now();
      pendingTimerRef.current = null;
      refresh();
    };
    if (pendingTimerRef.current !== null) {
      // A trailing refresh is already queued; let it absorb this event.
      return;
    }
    if (sinceLast >= MIN_INTERVAL_MS) {
      // Leading edge: run after a very short debounce to coalesce a burst.
      pendingTimerRef.current = window.setTimeout(run, TRAILING_DELAY_MS);
    } else {
      // Inside the throttle window: schedule at the end of the window.
      pendingTimerRef.current = window.setTimeout(
        run,
        Math.max(TRAILING_DELAY_MS, MIN_INTERVAL_MS - sinceLast),
      );
    }
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  // Cross-tab sync : quand un autre onglet change activeAccountId (via
  // switchAccount ou clear), on aligne l'onglet courant sans forcer de
  // refetch global (les hooks keyés par activeAccountId se réhydratent).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyIncoming = (incoming: string | null) => {
      setActiveAccountId((prev) => {
        if (incoming === prev) return prev;
        // Ne bascule que si le compte est accessible dans cet onglet, sinon
        // on garde la sélection locale (l'utilisateur pourra la corriger).
        if (incoming && !accounts.some((a) => a.account_id === incoming)) {
          return prev;
        }
        return incoming;
      });
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      applyIncoming(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    let bc: BroadcastChannel | null = null;
    try {
      if ("BroadcastChannel" in window) {
        bc = new BroadcastChannel(BROADCAST_CHANNEL);
        bc.onmessage = (evt) => {
          const next = (evt.data as any)?.activeAccountId ?? null;
          applyIncoming(typeof next === "string" ? next : null);
        };
      }
    } catch {
      /* ignore */
    }
    return () => {
      window.removeEventListener("storage", onStorage);
      if (bc) bc.close();
    };
  }, [accounts]);

  // Live-refresh accessible accounts whenever a collaborator row touching this
  // user changes (invitation accepted, permissions updated, access revoked…).
  // This avoids requiring a logout/login to pick up new module access.
  useEffect(() => {
    if (!user) return;
    const channel = subscribeChannel(`account-access-${user.id}`, (ch) => {
      ch.on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "account_collaborators",
            filter: `collaborator_id=eq.${user.id}`,
          },
          () => {
            scheduleRefresh();
          },
        );
    });
    return () => {
      channel();
    };
  }, [user, scheduleRefresh]);

  // NOTE: we intentionally do NOT refresh on window focus here. Doing so was
  // re-running every data hook downstream (useChecklists, useChecklistSections,
  // useProspects, dashboards…) on every tab refocus, producing a visible
  // "clignotement" / flash on every screen. The realtime subscription above
  // already covers permission changes; the email-matching fallback resolves
  // on the next manual reload, which is acceptable.

  const switchAccount = useCallback(
    (accountId: string) => {
      if (!accounts.some((a) => a.account_id === accountId)) return;
      if (accountId === activeAccountId) return;
      const previousAccountId = activeAccountId;
      setIsSwitching(true);
      writeStoredAccountId(accountId);
      try {
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
          const bc = new BroadcastChannel(BROADCAST_CHANNEL);
          bc.postMessage({ activeAccountId: accountId });
          bc.close();
        }
      } catch {
        /* ignore */
      }
      // Targeted soft-refresh:
      // - Hooks keyed by activeAccountId naturally produce a different
      //   queryKey once we flip state, so React Query serves cached data for
      //   the new workspace instantly (or fetches only what's missing).
      // - We only invalidate queries whose key mentions the previous or next
      //   account id, so unrelated data (global config, current user, static
      //   references…) is NOT refetched.
      // - `refetchType: "active"` refreshes just the mounted observers;
      //   inactive queries are marked stale and refetch lazily on remount.
      const touchesAccount = (key: readonly unknown[]) => {
        for (const part of key) {
          if (part === previousAccountId || part === accountId) return true;
          if (part && typeof part === "object") {
            for (const v of Object.values(part as Record<string, unknown>)) {
              if (v === previousAccountId || v === accountId) return true;
            }
          }
        }
        return false;
      };
      startTransition(() => {
        setActiveAccountId(accountId);
        queryClient.invalidateQueries({
          predicate: (query) => touchesAccount(query.queryKey as readonly unknown[]),
          refetchType: "active",
        });
      });
      // Hold the switching flag long enough for the overlay's fade-in +
      // fade-out to read as a single smooth transition (~250ms per phase).
      window.setTimeout(() => setIsSwitching(false), 450);
    },
    [accounts, activeAccountId, queryClient]
  );

  const activeAccount = useMemo(
    () => accounts.find((a) => a.account_id === activeAccountId) ?? null,
    [accounts, activeAccountId],
  );

  const isOwnAccount = useMemo(
    () => !!user && activeAccountId === user.id,
    [user, activeAccountId],
  );

  const hasPermission = useCallback(
    (module: string, level: "read" | "edit") => {
      if (!activeAccount) return false;
      if (activeAccount.is_owner) return true;
      const perm = activeAccount.permissions?.[module] ?? "none";
      if (perm === "none") return false;
      if (level === "read") return perm === "read" || perm === "edit";
      return perm === "edit";
    },
    [activeAccount]
  );

  const value = useMemo(
    () => ({
      activeAccountId,
      isOwnAccount,
      accounts,
      activeAccount,
      switchAccount,
      refresh,
      loading,
      isSwitching,
      hasPermission,
    }),
    [activeAccountId, isOwnAccount, accounts, activeAccount, switchAccount, refresh, loading, isSwitching, hasPermission],
  );

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccount must be used within AccountProvider");
  return ctx;
}

/**
 * Convenience hook returning the active account id, or the user's own id as fallback.
 * Use this in data hooks instead of `user.id` so that data scopes follow the workspace switcher.
 */
export function useActiveAccountId(): string | null {
  const { activeAccountId } = useAccount();
  return activeAccountId;
}
